import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

// Componentes y Modelos
import { toSignal } from '@angular/core/rxjs-interop';
import { BannerAppComponent } from '../../../components/banner-app/banner-app.component';
import { FooterAppComponent } from '../../../components/footer-app/footer-app.component';
import { MobilityLoaderComponent } from '../../components/mobility-loader/mobility-loader.component';
import { PendingInvoicesCardComponent } from '../../components/pending-invoices-card/pending-invoices-card.component';
import { PendingInvoicesSubscriptionComponent } from '../../components/pending-invoices-subscription/pending-invoices-subscription.component';
import { MembershipResponse } from '../../interfaces/membership.interface';
import {
  PaymentSchedule,
  PendingInvoicesApiResponse,
  PendingInvoicesDataCard,
  Policy,
} from '../../interfaces/pending-invoices.interface';
import { MembershipService } from '../../services/membership.service';
import { PendinInvoiocesService } from '../../services/pending-invoices.service';
import { TokenService } from '../../services/token.service';
import {
  formatCurrency,
  formatDate,
  getInvoiceStatus,
} from '../../utils/membership.utils';

@Component({
  selector: 'sura-pending-invoices-page',
  standalone: true,
  imports: [
    CommonModule,
    PendingInvoicesSubscriptionComponent,
    PendingInvoicesCardComponent,
    MobilityLoaderComponent,
    BannerAppComponent,
    FooterAppComponent,
  ],
  templateUrl: './pending-invoices-page.component.html',
  styleUrl: './pending-invoices-page.component.scss',
})
export class PendingInvoicesPageComponent {
  // --- INYECCIÓN DE DEPENDENCIAS ---
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private invoiceService = inject(PendinInvoiocesService);
  private membershipService = inject(MembershipService);
  private readonly TokenService = inject(TokenService);

  // --- ESTADO Y PARÁMETROS REACTIVOS ---
  public isPaying: WritableSignal<boolean> = signal(false);
  public subscriptionActive: WritableSignal<boolean> = signal(false);
  public showAnnualAlert: WritableSignal<boolean> = signal(false);
  public membershipType = computed(() =>
    this.subscriptionActive() ? ('subscription' as const) : ('payment' as const)
  );
  public isSubscriptionDisabled = computed(() =>
    this.selectedCard()?.paymentFrequency === 'annual'
  );
  public selectedCard: WritableSignal<PendingInvoicesDataCard | null> =
    signal(null);

  constructor() {
    // CA2: si el usuario activa el toggle con una tarjeta anual ya seleccionada → desactivar y alertar
    effect(() => {
      if (this.subscriptionActive() && this.selectedCard()?.paymentFrequency === 'annual') {
        this.subscriptionActive.set(false);
        this.showAnnualAlert.set(true);
      }
    }, { allowSignalWrites: true });
  }

  // --- FLUJO DE DATOS CENTRALIZADO ---
  private dataStream = toSignal(
    this.route.queryParams.pipe(
      tap(() => this.selectedCard.set(null)),
      switchMap((params) => {
        const userId = params['id'];
        const tokenResponseInput = params['input'];
        let paidInvoiceId: string | null = null;
        let productName = this.selectedCard()?.product || 'Motos';

        if (params['input']) {
          try {
            const decodedString = atob(params['input']); // Decodifica de Base64
            const inputObject = JSON.parse(decodedString); // Parsea el JSON
            paidInvoiceId = inputObject.recordId;
            productName = inputObject.product;
          } catch (e) {
            console.error('Error al decodificar el parámetro "input":', e);
            // Opcional: manejar el error, por ahora continuamos sin el ID de factura pagada
          }
        }

        if (!userId) {
          this.router.navigate(['seguros/movilidad/motos/cotizador']);
          return of({
            isLoading: false,
            error: 'ID de usuario no válido.',
            invoices: [],
            accountName: '',
          });
        }

        // Cadena de RxJS para obtener y procesar los datos
        return this.TokenService.initializeToken().pipe(
          take(1),
          switchMap(() => {
            return of(paidInvoiceId).pipe(
              switchMap((paidId) =>
                paidId
                  ? this.membershipService.getTokenResponse(
                    '',
                    paidId,
                    productName
                  )
                  : of(null)
              ),
              switchMap(() => this.invoiceService.getInvoices(userId)),
              map((response) => ({
                invoices: this.sortInvoices(this.transformToInsuranceCards(response)),
                accountName: response.accountName || '',
              })),
              map(({ invoices, accountName }) => ({ isLoading: false, error: null, invoices, accountName })),
              catchError((err) => {
                console.error('Error al cargar las facturas:', err);
                const errorMsg =
                  'No pudimos cargar tus facturas. Por favor, inténtalo de nuevo más tarde.';
                return of({ isLoading: false, error: errorMsg, invoices: [], accountName: '' });
              })
            );
          })
        );
      })
    ),
    // Estado inicial del stream
    { initialValue: { isLoading: true, error: null, invoices: [], accountName: '' } }
  );

  // --- SEÑALES DERIVADAS ---
  public invoices = computed(() => this.dataStream().invoices);
  public isLoading = computed(() => this.dataStream().isLoading);
  public error = computed(() => this.dataStream().error);
  public accountName = computed(() => {
    const name = this.dataStream().accountName;
    if (!name) return '';
    const first = name.split(' ')[0];
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  });

  /**
   * Ordena las facturas según las reglas de negocio actualizadas.
   */
  private sortInvoices(
    invoices: PendingInvoicesDataCard[]
  ): PendingInvoicesDataCard[] {
    return invoices.sort((a, b) => {
      // 1. Pagables vs. no pagables
      if (a.isPayable !== b.isPayable) {
        return a.isPayable ? -1 : 1;
      }
      // 2. Agrupación por póliza (orden alfabético)
      if (a.policy !== b.policy) {
        return a.policy.localeCompare(b.policy);
      }
      // 3. Orden por fecha de vencimiento dentro de cada póliza
      return a.dueDate.getTime() - b.dueDate.getTime();
    });
  }

  private transformToInsuranceCards(
    response: PendingInvoicesApiResponse
  ): PendingInvoicesDataCard[] {
    const oldestPendingInvoiceByPolicy: Record<string, string> = {};
    response.policies.forEach((policy) => {
      if (policy.paymentSchedules?.length > 0) {
        const sortedSchedules = [...policy.paymentSchedules].sort(
          (a, b) =>
            new Date(a.paymentDueDate).getTime() -
            new Date(b.paymentDueDate).getTime()
        );
        oldestPendingInvoiceByPolicy[policy.policy] =
          sortedSchedules[0].paymentScheduleId;
      }
    });
    const allInvoices: PendingInvoicesDataCard[] = [];
    response.policies.forEach((policy: Policy) => {
      policy.paymentSchedules.forEach((schedule: PaymentSchedule) => {
        const isPayable =
          oldestPendingInvoiceByPolicy[policy.policy] ===
          schedule.paymentScheduleId;
        const dueDate = new Date(schedule.paymentDueDate);
        dueDate.setUTCHours(12);
        const productName = policy.product || 'moto';
        allInvoices.push({
          id: schedule.paymentScheduleId,
          title: `Seguro de ${productName.toLowerCase()} digital`,
          plan: policy.plan,
          receiptNumber: schedule.documentNumber,
          policy: schedule.policy,
          isPayable,
          dueDate,
          paymentDate: formatDate(schedule.paymentDueDate),
          amount: formatCurrency(schedule.totalAmount),
          status: getInvoiceStatus(schedule.paymentDueDate, isPayable),
          product: productName,
          paymentFrequency: schedule.frequency?.toLowerCase() === 'annually' ? 'annual' : 'monthly',
        });
      });
    });
    return allInvoices;
  }

  handleCardSelection(card: PendingInvoicesDataCard): void {
    this.selectedCard.update((current) =>
      current?.id === card.id ? null : card
    );
    // CA2: si la tarjeta seleccionada es anual y el débito automático está activo, desactivarlo y alertar
    if (card.paymentFrequency === 'annual' && this.subscriptionActive()) {
      this.subscriptionActive.set(false);
      this.showAnnualAlert.set(true);
    }
  }

  dismissAnnualAlert(): void {
    this.showAnnualAlert.set(false);
  }

  /**
   * Maneja la solicitud de pago con la nueva validación de estado.
   */
  handlePayment(card: PendingInvoicesDataCard): void {
    this.isPaying.set(true);
    const quoteId = card.id;

    this.membershipService
      .getLightboxUrl('', quoteId, this.membershipType(), card.product)
      .pipe(finalize(() => this.isPaying.set(false)))
      .subscribe({
        next: (response: MembershipResponse) => {
          const status = response?.data?.status?.status;
          if (status && status !== 'OK' && status !== 'PENDING') {
            console.log(
              `Estado inicial no válido (${status}). Recargando para revalidar.`
            );
            this.reloadPageWithInvoice(quoteId, card.product);
            return;
          }

          if (response?.data?.processUrl) {
            const P = (window as any).P;
            if (P) {
              P.init(response.data.processUrl);
              P.on('close', () => {
                // Siempre recargamos al cerrar el lightbox para asegurar que la info esté actualizada.
                console.log(
                  'Lightbox cerrado. Recargando para actualizar estado.'
                );
                this.reloadPageWithInvoice(quoteId, card.product);
              });
            } else {
              console.error('La librería de pago "P" no se encontró.');
            }
          } else {
            console.error(
              'No se pudo obtener la URL de pago. Intente de nuevo.'
            );
          }
        },
        error: (err) => {
          console.error('Error al obtener la URL de pago:', err);
        },
      });
  }

  private reloadPageWithInvoice(
    invoiceId: string,
    product: string | undefined
  ): void {
    const inputObject = { recordId: invoiceId, product: product };

    const jsonString = JSON.stringify(inputObject);

    const base64Input = btoa(jsonString);

    // 4. Navegamos, reemplazando 'paidInvoiceId' por el nuevo 'input'
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { input: base64Input },
      queryParamsHandling: 'merge',
    });
  }

  volverIncio() {
    window.location.href = 'https://www.sura.co/';
  }
}
